import Controller from '@ember/controller';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import { task } from 'ember-concurrency';
import { tKey } from 'ember-intl';
import type AjaxService from 'lumen/services/ajax';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import { BI_UPSELL_CONTACT_LOCALSTORAGE_KEY } from 'lumen/utils/constants/general-constants';

type ContactRequest = { subscriptionId: string; user: string };

export default class HotelBiUpsellController extends Controller {
  @service declare ajax: AjaxService;
  @service declare alerting: Services['alerting'];
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare intl: Services['intl'];
  @service declare localStorage: Services['local-storage'];
  @service declare router: Services['router'];

  benefits = [
    tKey('upsell.bi.info_bullet.one_first'),
    tKey('upsell.bi.info_bullet.two_first'),
    tKey('upsell.bi.info_bullet.three_first'),
    tKey('upsell.bi.info_bullet.four_first'),
  ];

  sendGetInTouchTask = task({ restartable: true }, async (): Promise<void> => {
    try {
      await this.ajax.request('upsell/revenueinsight/', {
        method: 'POST',
        contentType: 'application/json',
        data: JSON.stringify({
          subscription_id: this.hotelId,
          page_url: window.location.href,
        }),
      });

      this.storeUpsellRequestInLocalStorage();

      await this.alerting.showSuccessAlert({
        title: this.intl.t('upsell.bi.contact_success_title'),
        text: this.intl.t('upsell.bi.contact_success_text'),
        buttons: {
          confirm: {
            text: this.intl.t('upsell.bi.contact_success_action'),
          },
        },
      });

      this.router.transitionTo('hotel.rates');
    } catch (error) {
      void this.alerting.showErrorAlert();
      this.errorTracking.captureException(error);
    }
  });

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hotelHasRequestedContact() {
    return this.isGetInTouchSentForSubscription(this.hotelId);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hotelId() {
    return this.hotelAndMarketService.hotel.id!;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isLoading() {
    return this.sendGetInTouchTask.isRunning;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  isGetInTouchSentForSubscription(subscriptionId: string) {
    try {
      if (this.localStorage.getItem(BI_UPSELL_CONTACT_LOCALSTORAGE_KEY)) {
        const storedContactRequests = JSON.parse(
          this.localStorage.getItem(
            BI_UPSELL_CONTACT_LOCALSTORAGE_KEY,
          ) as string,
        ) as ContactRequest[];

        // Check if we can find an object containing the current subscription id. This means the request has already been submitted for the current hotel.
        return storedContactRequests.some(
          (contactRequest) => contactRequest.subscriptionId === subscriptionId,
        );
      }
      return false;
    } catch (error) {
      this.errorTracking.captureException(error);
      return false;
    }
  }

  storeUpsellRequestInLocalStorage(): void {
    if (this.localStorage.getItem(BI_UPSELL_CONTACT_LOCALSTORAGE_KEY)) {
      // add the current subscription if it's not found in localStorage
      if (!this.isGetInTouchSentForSubscription(this.hotelId)) {
        const storedContactRequests = JSON.parse(
          this.localStorage.getItem(
            BI_UPSELL_CONTACT_LOCALSTORAGE_KEY,
          ) as string,
        ) as ContactRequest[];

        storedContactRequests.push({
          user: this.hotelAndMarketService.user.email,
          subscriptionId: this.hotelId,
        });

        this.localStorage.setItem(
          BI_UPSELL_CONTACT_LOCALSTORAGE_KEY,
          JSON.stringify(storedContactRequests),
        );
      }
    } else {
      // add a stringified array containing the current subscription in localStorage
      this.localStorage.setItem(
        BI_UPSELL_CONTACT_LOCALSTORAGE_KEY,
        JSON.stringify([
          {
            user: this.hotelAndMarketService.user.email,
            subscriptionId: this.hotelId,
          },
        ]),
      );
    }
  }

  @action onImageLoaded(event: Event): void {
    const parent = (event.target as HTMLElement).parentNode;
    if (parent instanceof HTMLElement) {
      parent.style.opacity = '1';
    }
  }

  @action sendGetInTouch(): void {
    void this.sendGetInTouchTask.perform();
  }
}
